// src/components/ui/calendar/core/CalendarOverflowIndicator/CalendarOverflowIndicator.jsx
import React from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';

// shadcn components
import { Button } from '../../../button';

import { cn } from '../../../../../utils/cn';

const CalendarOverflowIndicator = ({
  count,
  hasPreview = false,
  onClick,
  variant = 'default', // 'default', 'preview', 'compact'
  size = 'sm', // 'xs', 'sm', 'md'
  className = ''
}) => {
  // Size configurations
  const sizeConfig = {
    xs: {
      container: 'px-1 py-0.5 text-xs h-auto',
      icon: 'w-3 h-3'
    },
    sm: {
      container: 'px-2 py-1 text-xs h-auto',
      icon: 'w-3 h-3'
    },
    md: {
      container: 'px-3 py-1.5 text-sm h-auto',
      icon: 'w-4 h-4'
    }
  };

  // Variant styles
  const variantStyles = {
    default: cn(
      "bg-slate-600/30 hover:bg-slate-600/50 text-slate-300 hover:text-slate-200",
      "border border-slate-500/30 hover:border-slate-400/50"
    ),
    preview: cn(
      "bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 hover:text-blue-200",
      "border border-blue-500/30 hover:border-blue-400/50",
      "animate-pulse"
    ),
    compact: cn(
      "bg-slate-700/40 hover:bg-slate-700/60 text-slate-400 hover:text-slate-300",
      "border border-slate-600/40"
    )
  };

  const currentSize = sizeConfig[size];
  const IconComponent = hasPreview ? Plus : MoreHorizontal;

  // Determine text to display
  const getText = () => {
    if (hasPreview && count > 0) {
      return `+${count + 1} more`;
    }
    if (hasPreview) {
      return '+1 preview';
    }
    return `+${count} more`;
  };

  // Don't render if no overflow
  if (count === 0 && !hasPreview) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        // Base styles
        "inline-flex items-center gap-1 rounded-md font-medium",
        "transition-all duration-200 cursor-pointer",
        "hover:scale-105 active:scale-95",

        // Size and variant styles
        currentSize.container,
        variantStyles[hasPreview ? 'preview' : variant],

        className
      )}
      title={hasPreview ? 'View preview and other events' : 'View all events'}
    >
      <IconComponent className={currentSize.icon} />
      <span>{getText()}</span>
    </Button>
  );
};

export default CalendarOverflowIndicator;